import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Seo, { SITE } from "@/components/site/Seo";
import { breadcrumbJsonLd } from "@/lib/schema";

const UPDATED = "July 2026";

const sections = [
  {
    h: "Who we are",
    body: [
      "Double Rainbow Suites (\"we\", \"us\") operates short-term and vacation rentals and property-management services in Wilton Manors and Fort Lauderdale, Florida. This policy explains what information we collect through this website, how we use it, and the choices you have.",
    ],
  },
  {
    h: "Information we collect",
    body: [
      "When you submit our contact or property-management forms, we collect the details you provide — such as your name, email, phone number, and any message or property information you include. We use this only to respond to your enquiry.",
      "We also collect limited technical and usage information automatically (for example pages visited and general device/browser data) through the analytics tools described below.",
    ],
  },
  {
    h: "Cookies & analytics",
    body: [
      "We use cookies and similar technologies to understand site traffic and improve your experience. Analytics and marketing cookies load only after you accept them via our consent banner — you can decline at any time and essential site features will still work.",
      "Google Analytics 4 helps us understand how visitors use the site. Meta (Facebook) Pixel helps us measure and improve any advertising we run on Facebook and Instagram. Both are governed by Google Consent Mode and Meta consent settings, and remain disabled until you opt in.",
    ],
  },
  {
    h: "Third-party services",
    body: [
      "Bookings are completed on our secure booking partner, Hospitable (Hospitable Direct), on their own platform and subject to their privacy policy. Enquiry emails are delivered via our email provider (Resend). Local weather is shown using the Open-Meteo service. We do not sell your personal information.",
    ],
  },
  {
    h: "Your choices & rights",
    body: [
      "You can accept or decline analytics and marketing cookies through our consent banner, and change your browser settings to manage cookies at any time. Depending on where you live (including the EU/UK under GDPR and California under the CCPA/CPRA), you may have the right to access, correct, or delete your personal information, and to opt out of certain processing. To make a request, contact us using the details below.",
    ],
  },
  {
    h: "Contact us",
    body: [
      "Double Rainbow Suites — 1425 NE 24th St, Wilton Manors, FL 33305. Phone: (954) 332-5725. You can also reach us any time through our contact page and we'll respond promptly.",
    ],
  },
];

export default function Privacy() {
  return (
    <main className="pt-32 md:pt-40 pb-24 md:pb-32">
      <Seo
        title="Privacy & Cookie Policy | Double Rainbow Suites"
        description="How Double Rainbow Suites collects, uses and protects your information, our use of cookies and analytics, third-party services, and your privacy rights under GDPR and CCPA."
        canonical={`${SITE}/privacy`}
        jsonLd={breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Privacy & Cookie Policy", path: "/privacy" },
        ])}
      />
      <div className="max-w-[900px] mx-auto px-6 md:px-10">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <p className="overline text-sunset mb-5">Legal</p>
          <h1 className="font-display text-5xl md:text-7xl leading-[0.9] tracking-tight">
            Privacy &amp; Cookie Policy
          </h1>
          <p className="text-emerald-soft mt-6 font-light">Last updated: {UPDATED}</p>
        </motion.div>

        <div className="mt-14 flex flex-col gap-10 md:gap-12" data-testid="privacy-content">
          {sections.map((s, i) => (
            <motion.section
              key={i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 2) * 0.05 }}
              className="border-t border-emerald/12 pt-7"
            >
              <h2 className="font-display text-2xl md:text-3xl text-emerald mb-4 leading-snug">{s.h}</h2>
              {s.body.map((p, j) => (
                <p key={j} className="text-emerald-soft leading-relaxed font-light mb-4 last:mb-0">{p}</p>
              ))}
            </motion.section>
          ))}
        </div>

        <div className="mt-14">
          <Link to="/contact" data-testid="privacy-contact-btn" className="group inline-flex items-center gap-2 rounded-full bg-sunset px-8 py-4 text-sand font-medium transition-colors duration-300 hover:bg-emerald">
            Contact us <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
          </Link>
        </div>
      </div>
    </main>
  );
}
